import { useState, useEffect } from "react";
import { api } from "../api";
import { useUsdRate } from "../useUsdRate";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Analytics({ token }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [tz, setTz]           = useState("America/Los_Angeles");
  const [search, setSearch]   = useState("");
  const [page, setPage]       = useState(0);
  const PAGE_SIZE = 25;
  const usdRate = useUsdRate(token);

  useEffect(() => {
    api.analytics(token)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message || "Failed to load"); setLoading(false); });
    api.getConfig(token)
      .then(cfg => { if (cfg?.Timezone) setTz(cfg.Timezone); })
      .catch(() => {});
  }, [token]);

  if (loading) return <div style={s.empty}>Loading analytics…</div>;
  if (error)   return <div style={{...s.empty, color:"#ef4444"}}>⚠ {error}</div>;
  if (!data)   return <div style={s.empty}>No data</div>;

  const sales   = data.sales || data.recent_sales || [];
  const netUSD  = ((data.total_net  || 0) / 1000 * usdRate).toFixed(2);
  const grossUSD= ((data.total_gross|| 0) / 1000 * usdRate).toFixed(2);

  // Chart: group by day
  const byDay = {};
  sales.forEach(s => {
    const day = new Date(s.created_at).toLocaleDateString("en-US", { month:"short", day:"numeric", timeZone: tz });
    byDay[day] = (byDay[day] || 0) + (s.amount || 0);
  });
  const chartData = Object.entries(byDay).map(([day, amount]) => ({ day, amount })).reverse();

  // Filtered sales table
  const filtered = search
    ? sales.filter(s => (s.item_name || "").toLowerCase().includes(search.toLowerCase()))
    : sales;
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageSlice  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit", timeZone: tz }); }
    catch { return iso; }
  };

  return (
    <div>
      {/* Stat cards */}
      <div style={s.cards}>
        {[
          ["Total Sales",   data.total_sales ?? 0],
          ["Net Robux",     (data.total_net  || 0).toLocaleString() + " R"],
          ["Net USD",       "$" + netUSD],
          ["Gross Robux",   (data.total_gross|| 0).toLocaleString() + " R"],
          ["Gross USD",     "$" + grossUSD],
        ].map(([label, val]) => (
          <div key={label} style={s.card}>
            <div style={s.cardLabel}>{label}</div>
            <div style={s.cardVal}>{val}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Daily Revenue (R$)</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
              <XAxis dataKey="day" stroke="#333" tick={{fill:"#555", fontSize:11}} />
              <YAxis stroke="#333" tick={{fill:"#555", fontSize:11}} />
              <Tooltip contentStyle={{background:"#111", border:"1px solid #222", color:"#fff", fontSize:12}} formatter={v => [v.toLocaleString() + " R", "Net"]} />
              <Line type="monotone" dataKey="amount" stroke="var(--rbx-accent,#38bdf8)" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top items */}
      {(data.top_items || []).length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Top Selling Items</div>
          <div style={{display:"flex", flexWrap:"wrap", gap:8}}>
            {(data.top_items || []).map((item, i) => (
              <div key={i} style={s.topChip}>
                <span style={{color:"#555", marginRight:6}}>#{i+1}</span>
                <span style={{color:"#ccc"}}>{item.name}</span>
                <span style={{color:"var(--rbx-accent,#38bdf8)", marginLeft:8, fontWeight:700}}>{item.count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full sales table */}
      <div style={s.section}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8}}>
          <div style={s.sectionTitle}>Sales History ({filtered.length}{filtered.length !== sales.length ? ` of ${sales.length}` : ""})</div>
          <input
            style={s.search}
            placeholder="Search item name…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
        </div>

        {filtered.length === 0
          ? <div style={s.empty}>No sales yet. The bot records a sale every time it successfully lists an item.</div>
          : <>
            <table style={s.table}>
              <thead>
                <tr>{["","Item","Net R$","Net USD","Gross R$","Date"].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {pageSlice.map((sale, i) => {
                  const net   = sale.amount || 0;
                  const gross = sale.gross  || Math.round(net / 0.7) || 0;
                  const usd   = (net / 1000 * usdRate).toFixed(2);
                  return (
                    <tr key={i} style={{background: i % 2 === 0 ? "transparent" : "#0a0a0a"}}>
                      <td style={{...s.td, width:40, paddingRight:0}}>
                        {sale.thumbnail
                          ? <img src={sale.thumbnail} alt="" style={{width:32, height:32, borderRadius:4, display:"block"}} onError={e => e.target.style.display="none"} />
                          : <div style={{width:32, height:32, borderRadius:4, background:"#1a1a1a"}} />
                        }
                      </td>
                      <td style={s.td}>{sale.item_name || "Unknown"}</td>
                      <td style={{...s.td, color:"var(--rbx-accent,#38bdf8)", fontWeight:600}}>{net.toLocaleString()}</td>
                      <td style={{...s.td, color:"#4ade80"}}>${usd}</td>
                      <td style={{...s.td, color:"#888"}}>{gross.toLocaleString()}</td>
                      <td style={{...s.td, color:"#555"}}>{fmtDate(sale.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginTop:14}}>
                <button style={s.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>←</button>
                <span style={{color:"#555", fontSize:12}}>Page {page + 1} of {totalPages}</span>
                <button style={s.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>→</button>
              </div>
            )}
          </>
        }
      </div>
    </div>
  );
}

const s = {
  empty:       { color:"#555", padding:40 },
  cards:       { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12, marginBottom:20 },
  card:        { background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:"16px 18px" },
  cardLabel:   { color:"#555", fontSize:11, marginBottom:6, letterSpacing:1 },
  cardVal:     { color:"#fff", fontSize:20, fontWeight:700 },
  section:     { background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8, padding:20, marginBottom:16 },
  sectionTitle:{ color:"#555", fontSize:11, letterSpacing:1, marginBottom:0 },
  topChip:     { display:"flex", alignItems:"center", background:"#111", border:"1px solid #1a1a1a", borderRadius:6, padding:"6px 12px", fontSize:12 },
  table:       { width:"100%", borderCollapse:"collapse" },
  th:          { textAlign:"left", color:"#555", fontSize:11, padding:"6px 10px", borderBottom:"1px solid #1a1a1a", letterSpacing:1 },
  td:          { padding:"8px 10px", borderBottom:"1px solid #111", fontSize:12, color:"#ccc", verticalAlign:"middle" },
  search:      { background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, padding:"7px 12px", color:"#fff", fontSize:12, outline:"none", width:200 },
  pageBtn:     { padding:"6px 14px", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, color:"#888", cursor:"pointer", fontSize:13, fontFamily:"inherit" },
};
